import { BulkApprovalBar } from "../BulkApprovalBar";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useSelector } from "react-redux";
import { customGetAPI, customAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import {
  StatusBadge,
  displayFlexItem,
  getStatusFlow,
} from "services/constants";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  bulkSelectionCheckboxSx,
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import {
  ShiftCorrectionStatusCards,
  canonicalShiftCorrectionStatus,
  shiftCorrectionStatusVisual,
} from "./ShiftCorrectionStatusCards";
import { ShiftCorrectionDetailPanel } from "./ShiftCorrectionDetailPanel";
import { ApplyShiftChange } from "./ApplyShiftChange";
import {
  SHIFT_CORRECTION_HEADERS,
  toApproveShiftChangeRecord,
  toApproveFilterRow,
} from "./shiftCorrectionApproveHelpers";
import moment from "moment";
import { resolveRangeLabel } from "../dateRangeUtils";

const SHIFT_CHANGE_API =
  "team_zone/approve/shift_change/teamzone-shift-change/";

export const ShiftCorrection = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const { domain, userDetails, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterData, setFilterData] = useState([]);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  const [selectedData, setSelectedData] = useState(null);
  const [viewDetails, setViewDetails] = useState(false);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  // Initialized from (and kept in sync with) the Approval Center landing
  // page's own dateRangeLabel prop — previously hardcoded to "Last 7 Days"
  // regardless of the actual startDate/endDate this screen was handed, on
  // a fresh ?view= deep link or Team Workspace Home's "date" param. A
  // manual re-pick on this screen's own picker still overrides it locally.
  const [dateRangeLabel, setDateRangeLabel] = useState(
    dateRangeLabelProp || "Last 7 Days",
  );

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);

  const { expanded: panelExpanded, toggleExpanded: togglePanelExpanded } =
    useExpandablePanel(viewDetails);
  const {
    expanded: createPanelExpanded,
    toggleExpanded: toggleCreatePanelExpanded,
  } = useExpandablePanel(isCreatePanelOpen);
  // Measures the sticky footer bar's own height so the table can reserve
  // that much bottom padding — otherwise its last row would sit hidden
  // behind the footer once the page scrolls that footer's sticky trigger
  // into effect. Ported from Administration's Users list / TeamZone
  // Approve > Leave, which already handle this correctly.
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  // Same measurement Administration's ListView/Companies' ListView use —
  // CustomFilter's chip row only appears once a filter/sort is applied, so
  // the sticky table head below it needs this measured (not guessed) to
  // know how far down to offset itself.
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    const fetchShiftChangeDetails = async () => {
      const res = await customGetAPI(
        `${domain}${SHIFT_CHANGE_API}?user_id=${userDetails?.id}&company_id=${topbarCompanyId}&start_date=${startDate}&end_date=${endDate}`,
      );
      const rows = Array.isArray(res?.responseDetails)
        ? res.responseDetails
        : (res?.responseDetails?.results ?? []);
      const mappedRecords = rows.map(toApproveShiftChangeRecord);
      setRecords(mappedRecords);
      // Also seed the filtered view directly (same shape CustomFilter's own
      // effect would produce from filterInputRows) — that effect fires a
      // render later, so without this the table flashes "No Matching
      // Results" for a frame right as loading ends.
      setFilterData(mappedRecords.map(toApproveFilterRow));
      setLoading(false);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) {
      setLoading(true);
      fetchShiftChangeDetails();
    }
  }, [
    domain,
    endDate,
    reCallApi,
    setReCallApi,
    startDate,
    topbarCompanyId,
    userDetails?.id,
  ]);

  // Checks its own "Schedule Change" submodule rather than sharing
  // WorkAdjustment's bucket — the backend doesn't provision this submodule
  // yet, so this reads as no access until it's added there.
  const check_access =
    checkEditorPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Schedule Change",
    ) ||
    checkEditorPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Shift Change",
    );

  const filterInputRows = useMemo(
    () => records.map(toApproveFilterRow),
    [records],
  );

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setFilterData(filtered);
      if (page * rowsPerPage >= filtered.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const statusCounts = useMemo(
    () =>
      records.reduce((acc, r) => {
        const status = canonicalShiftCorrectionStatus(r.status);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [records],
  );

  const visibleData = useMemo(() => {
    if (!statusCardFilter) return filterData;
    return filterData.filter(
      (r) => canonicalShiftCorrectionStatus(r.status) === statusCardFilter,
    );
  }, [filterData, statusCardFilter]);

  const handleStatusCardClick = (key) => {
    clearSelection();
    setShowBulkRejectDialog(false);
    setPage(0);
    setExpandedIndex(null);
    setExpandedData(null);
    setViewDetails(false);
    setSelectedData(null);
    setStatusCardFilter(key === "All" ? "" : key);
  };

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_shift_correction_saved_views",
    fullHeaders: SHIFT_CORRECTION_HEADERS,
    screenAccess: check_access,
    onViewChange: () => setPage(0),
  });

  const isSelected = (row) => selectedRowKeys.has(row.id);
  const pendingRows = useMemo(
    () => visibleData.filter((r) => r.status === "Pending"),
    [visibleData],
  );

  const toggleRow = (row) => {
    if (row.status !== "Pending") return;
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
      return next;
    });
  };

  const toggleAll = () => {
    const allSelected =
      pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
    setSelectedRowKeys(
      allSelected ? new Set() : new Set(pendingRows.map((r) => r.id)),
    );
  };

  const isAllSelected =
    pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
  const isIndeterminate =
    selectedRowKeys.size > 0 && selectedRowKeys.size < pendingRows.length;

  const clearSelection = () => {
    setSelectedRowKeys(new Set());
    setBulkRejectReason("");
  };

  const putShiftChangeStatus = async (ids, status, reason) => {
    const payload = {
      ids: Array.from(ids),
      status,
      ...(status === "Rejected" && { rejected_reason: reason || "" }),
      // No dedicated cancel-an-approved-shift-change endpoint exists yet,
      // same gap as Comp Off — this reuses the same approve/reject endpoint
      // with status: "Cancelled" as a first attempt, needs verifying
      // against the real backend.
      ...(status === "Cancelled" && { cancelled_reason: reason || "" }),
    };
    return customAPI(`${domain}${SHIFT_CHANGE_API}`, "PUT", payload);
  };

  const handleApprove = async (record) => {
    setActionLoading(true);
    const response = await putShiftChangeStatus(
      new Set([record.id]),
      "Approved",
    );
    if (response?.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Shift change request approved.",
      );
      setViewDetails(false);
      setReCallApi(true);
    } else {
      showToast(
        "error",
        response?.message ||
          "Couldn't approve the schedule change request. Please try again.",
      );
    }
    setActionLoading(false);
  };

  const handleReject = async (record, reason) => {
    setActionLoading(true);
    const response = await putShiftChangeStatus(
      new Set([record.id]),
      "Rejected",
      reason,
    );
    if (response?.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Shift change request rejected.",
      );
      setViewDetails(false);
      setReCallApi(true);
    } else {
      showToast(
        "error",
        response?.message ||
          "Couldn't reject the schedule change request. Please try again.",
      );
    }
    setActionLoading(false);
  };

  const handleCancel = async (record, reason) => {
    setActionLoading(true);
    const response = await putShiftChangeStatus(
      new Set([record.id]),
      "Cancelled",
      reason,
    );
    if (response?.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Shift change request cancelled.",
      );
      setViewDetails(false);
      setReCallApi(true);
    } else {
      showToast(
        "error",
        response?.message ||
          "Couldn't cancel the schedule change request. Please try again.",
      );
    }
    setActionLoading(false);
  };

  const handleBulkApprove = async () => {
    if (!selectedRowKeys.size) return;
    setBulkLoading(true);
    const response = await putShiftChangeStatus(selectedRowKeys, "Approved");
    if (response?.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Records approved.",
      );
      clearSelection();
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Failed to approve");
    }
    setBulkLoading(false);
  };

  const handleBulkReject = async () => {
    if (!selectedRowKeys.size) return;
    setBulkLoading(true);
    const response = await putShiftChangeStatus(
      selectedRowKeys,
      "Rejected",
      bulkRejectReason,
    );
    if (response?.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Records rejected.",
      );
      clearSelection();
      setShowBulkRejectDialog(false);
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Failed to reject");
    }
    setBulkLoading(false);
  };

  const handleShiftChangeDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  const newRequestButton = (
    <NewRequestButton
      hasAccess={check_access}
      deniedMessage="Access denied: You are not authorized to create a schedule change request"
      onClick={() => setIsCreatePanelOpen(true)}
    />
  );

  const reportMetadata = useMemo(
    () => ({
      "User Name": userDetails?.username || userDetails?.user_name || "N/A",
      Type: "Schedule Change",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, userDetails],
  );

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box sx={{ flexGrow: 1, width: "100%", overflow: "visible" }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>
              Schedule Change
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review and manage schedule change requests awaiting approval
            </Mui.Typography>
          </Box>

          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleShiftChangeDateRange}
              includeCurrentDate
              showToday
            />
            {newRequestButton}
          </Mui.Stack>
        </Box>

        <ShiftCorrectionStatusCards
          counts={statusCounts}
          activeFilter={statusCardFilter}
          onCardClick={handleStatusCardClick}
        />

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title="Schedule Change Requests"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={filterInputRows}
            filteredData={visibleData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            showSaveButton={true}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            labeledToolbar={true}
          />
        </Box>

        <BulkApprovalBar
          selectedCount={statusCardFilter === "Pending" ? selectedRowKeys.size : 0}
          loading={bulkLoading}
          onReject={() => setShowBulkRejectDialog(true)}
          onApprove={handleBulkApprove}
        />

        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            sx={{
              width: "100%",
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
            <ApproveTable
              stickyHeader
              sx={{ minWidth: 920, width: "100%", tableLayout: "fixed" }}
            >
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {statusCardFilter === "Pending" && (
                    <Mui.TableCell
                      padding="checkbox"
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      {statusCardFilter === "Pending" && pendingRows.length > 0 && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={toggleAll}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                  <Mui.TableCell
                    sx={{
                      ...approveTableHeadCell,
                      top: APP_BAR_OFFSET + customFilterHeight,
                      width: 40,
                    }}
                  />
                  {visibleHeaders?.map((value) => (
                    <Mui.TableCell
                      key={value.key}
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      <TruncatedTableValue value={value?.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleData?.length === 0 ? (
                  <TableEmptyState
                    dataLength={filterInputRows}
                    colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                  />
                ) : (
                  visibleData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((value, index) => (
                      <React.Fragment key={value.id}>
                        <Mui.TableRow
                          onClick={() => {
                            setSelectedData(value);
                            setViewDetails(true);
                          }}
                          sx={{
                            cursor: "pointer",
                            ...designShell.tableRowHover,
                            ...(viewDetails && value.id === selectedData?.id
                              ? designShell.neutralSelectedRow
                              : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          {statusCardFilter === "Pending" && (
                            <Mui.TableCell
                              padding="checkbox"
                              sx={approveTableBodyCell}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {statusCardFilter === "Pending" && value.status === "Pending" && (
                                <Mui.Checkbox
                                  size="small"
                                  sx={bulkSelectionCheckboxSx}
                                  checked={isSelected(value)}
                                  onChange={() => toggleRow(value)}
                                />
                              )}
                            </Mui.TableCell>
                          )}
                          <Mui.TableCell
                            sx={approveTableBodyCell}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedIndex((prev) =>
                                prev === index ? null : index,
                              );
                              setExpandedData(value);
                            }}
                          >
                            <ArrowRightIcon
                              sx={{
                                color: designColors.slate500,
                                cursor: "pointer",
                                transition: "transform 0.2s ease",
                                transform:
                                  expandedIndex === index
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                              }}
                            />
                          </Mui.TableCell>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell
                              key={head.key} data-label={head.name}
                              sx={approveTableBodyCell}
                            >
                              {head.key === "employee_name" ? (
                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Mui.Avatar
                                    sx={{ width: 28, height: 28 }}
                                    alt={value?.employee_name
                                      ?.charAt(0)
                                      .toUpperCase()}
                                    src={value?.image_url}
                                  />
                                  <Mui.Typography sx={{ fontSize: 14 }}>
                                    {value?.employee_name || "N/A"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              ) : head.key === "status" ? (
                                <Mui.Box sx={displayFlexItem}>
                                  <StatusBadge
                                    color={
                                      value?.color_code ||
                                      shiftCorrectionStatusVisual(value?.status)
                                        .accent
                                    }
                                    label={value?.status || "N/A"}
                                  />
                                </Mui.Box>
                              ) : (
                                (<TruncatedTableValue value={value[head.key] ?? "N/A"} />)
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>

                        {expandedIndex === index && (
                          <Mui.TableRow>
                            <Mui.TableCell
                              colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                              sx={{
                                p: 2,
                                borderBottom: `1px solid ${designColors.slate200}`,
                              }}
                            >
                              {getStatusFlow(expandedData)[
                                expandedData?.status
                              ]?.map((item, i, arr) => (
                                <StatusRow
                                  key={i}
                                  status={item.status}
                                  user={item.user}
                                  date={item.date}
                                  showConnector={i !== arr.length - 1}
                                />
                              ))}
                              <ApprovalAuditTrail record={expandedData} compact />
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleData?.length || 0,
              itemLabel: "schedule change requests",
              onPageChange: setPage,
              onRowsPerPageChange: (event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              },
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>

      <ApplyShiftChange
        open={isCreatePanelOpen}
        onClose={() => setIsCreatePanelOpen(false)}
        setReCallApi={setReCallApi}
        expanded={createPanelExpanded}
        onToggleExpand={toggleCreatePanelExpanded}
      />

      <ShiftCorrectionDetailPanel
        open={viewDetails}
        onClose={() => setViewDetails(false)}
        record={selectedData}
        checkAccess={check_access}
        onApprove={handleApprove}
        onReject={handleReject}
        onCancel={handleCancel}
        actionLoading={actionLoading}
        expanded={panelExpanded}
        onToggleExpand={togglePanelExpanded}
      />

      <GlobalConfirmDialog
        open={showBulkRejectDialog}
        title="Reject Selected Requests"
        onCancel={() => {
          setShowBulkRejectDialog(false);
          setBulkRejectReason("");
        }}
        onConfirm={handleBulkReject}
        confirmLabel="Reject"
        cancelLabel="Cancel"
        loading={bulkLoading}
        danger
        reasonValue={bulkRejectReason}
        onReasonChange={setBulkRejectReason}
        reasonPlaceholder="Enter a reason for rejection"
        reasonMinRows={2}
        maxWidth="sm"
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Reason (optional) - applied to all selected schedule change requests.
        </Mui.Typography>
      </GlobalConfirmDialog>
    </React.Fragment>
  );
};

export default ShiftCorrection;
